/**
 * 版本信息接口
 */
export interface VersionInfo {
  version: string;
  updatedAt: string;
  content: string[];
}

/**
 * 獲取版本信息
 */
export async function getVersionInfo(): Promise<VersionInfo | null> {
  try {
    const response = await fetch("/version.json");
    if (!response.ok) {
      throw new Error("Failed to fetch version info");
    }
    const data = await response.json();
    return data as VersionInfo;
  } catch (error) {
    console.error("Error loading version info:", error);
    return null;
  }
}

/**
 * 獲取當前環境信息
 */
export function getEnvironment(): {
  name: string;
  projectId: string;
  isProduction: boolean;
} {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || "";
  const mode = import.meta.env.MODE || "dev";

  // 根據 projectId 判斷環境
  if (projectId === "onreel-dev") {
    return {
      name: "正式環境",
      projectId: "onreel-dev",
      isProduction: true,
    };
  } else if (projectId === "onreel-staging") {
    return {
      name: "測試環境",
      projectId: "onreel-staging",
      isProduction: false,
    };
  }

  // 如果無法判斷，根據 mode 推斷
  return {
    name: mode === "production" ? "正式環境" : "測試環境",
    projectId: projectId || "unknown",
    isProduction: mode === "production",
  };
}

/**
 * 檢查伺服器健康狀態
 * 通過嘗試讀取 Firestore 來檢查連接狀態
 */
export async function checkServerHealth(): Promise<boolean> {
  try {
    const { db } = await import("@/services/firebase/init");
    const { doc, getDoc } = await import("firebase/firestore");
    
    // 嘗試讀取一個不存在的文檔來測試連接
    // 使用一個不存在的集合和文檔 ID，這樣不會產生實際的讀取操作
    const testDocRef = doc(db, "_health_check", "ping");
    
    // 設置超時（3秒）
    const timeoutPromise = new Promise<boolean>((_, reject) => {
      setTimeout(() => reject(new Error("Health check timeout")), 3000);
    });
    
    const healthCheckPromise = getDoc(testDocRef)
      .then(() => {
        // 如果成功讀取（雖然文檔不存在），連接正常
        return true;
      })
      .catch((error: any) => {
        // 如果是權限錯誤或文檔不存在，這表示連接正常（只是文檔不存在或沒有權限）
        // 如果是網絡錯誤或其他連接問題，則返回 false
        const errorCode = error?.code || "";
        if (errorCode === "permission-denied" || errorCode === "not-found" || errorCode === "unavailable") {
          // unavailable 可能是暫時的網絡問題，但我們仍然認為連接正常
          // 只有在明確的網絡錯誤時才返回 false
          return true;
        }
        // 其他錯誤（如網絡錯誤、超時等）表示連接異常
        console.warn("Server health check error:", errorCode, error?.message);
        return false;
      });
    
    return await Promise.race([healthCheckPromise, timeoutPromise]);
  } catch (error) {
    console.error("Server health check failed:", error);
    return false;
  }
}

